/** Results derived from the document, computed once per document version and shared by every panel. */
import { assessProject, type ProjectResults } from '../engine';
import { checkEquipment, lintProject, zoneInstances, type EquipmentCheck, type Lint, type ZoneInstance } from '../geometry';
import type { ProjectDoc } from '../model/types';
import { useStore } from './store';

export interface Derived {
  results: ProjectResults;
  zones: ZoneInstance[];
  checks: Record<string, EquipmentCheck>;
  lints: Lint[];
  /** Milliseconds the last recompute took. */
  ms: number;
}

const cache = new WeakMap<ProjectDoc, Derived>();

export function derive(doc: ProjectDoc): Derived {
  const hit = cache.get(doc);
  if (hit) return hit;
  const t0 = performance.now();
  const results = assessProject(doc);
  const zones = zoneInstances(doc, results);
  const checks = Object.fromEntries(doc.equipment.map((e) => [e.id, checkEquipment(e, zones)]));
  const lints = lintProject(doc, results, zones);
  const d: Derived = { results, zones, checks, lints, ms: performance.now() - t0 };
  cache.set(doc, d);
  return d;
}

export function useDerived(): Derived | null {
  const doc = useStore((s) => s.doc);
  return doc ? derive(doc) : null;
}
